<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store.js';
import { messageStamp } from 'shared/helpers/timeHelper';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Button from 'dashboard/components-next/button/Button.vue';
import PageLayout from 'dashboard/components-next/metaAgent/PageLayout.vue';
import StatusCard from 'dashboard/components-next/metaAgent/StatusCard.vue';

const WHATSAPP_MANAGER_URL = 'https://business.facebook.com/wa/manage/home/';

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const inboxes = useMapGetter('metaAgent/getInboxes');
const agent = useMapGetter('metaAgent/getAgent');
const eligibility = useMapGetter('metaAgent/getEligibility');
const uiFlags = useMapGetter('metaAgent/getUIFlags');

const isAttesting = ref(false);

const inboxId = computed(() => Number(route.params.inboxId));
const inbox = computed(
  () => inboxes.value.find(item => item.id === inboxId.value) || {}
);
const isConnected = computed(() => Boolean(agent.value.id));
const isOnboarded = computed(
  () => isConnected.value && agent.value.lifecycle !== 'pending_setup'
);
const captainAttending = computed(() => inbox.value.captain_attending);
const isEligible = computed(() => eligibility.value[inboxId.value]);

const lifecycleLabel = computed(() => {
  const key = (agent.value.lifecycle || 'not_connected').toUpperCase();
  return t(`META_AGENT.LIFECYCLE.${key}`);
});

const attestedLabel = computed(() =>
  agent.value.terms_attested_at
    ? messageStamp(agent.value.terms_attested_at, 'LLL d, yyyy')
    : ''
);

// Falls back to a name-less sentence when the attester has been removed from
// the account, rather than rendering "Recorded by  on <date>".
const termsDescription = computed(() => {
  if (!agent.value.terms_attested_at) {
    return t('META_AGENT.OVERVIEW.TERMS.DESCRIPTION');
  }
  return agent.value.terms_attested_by
    ? t('META_AGENT.OVERVIEW.TERMS.ATTESTED', {
        name: agent.value.terms_attested_by,
        date: attestedLabel.value,
      })
    : t('META_AGENT.OVERVIEW.TERMS.ATTESTED_UNKNOWN', {
        date: attestedLabel.value,
      });
});

// Meta's `detail` names the offending field; the generic error is the fallback.
const alertFailure = (error, fallbackKey) => {
  const data = error?.response?.data || {};
  useAlert(data.detail || data.error || t(fallbackKey));
};

const load = async () => {
  try {
    await store.dispatch('metaAgent/getInboxes');
    await store.dispatch('metaAgent/getAgent', inboxId.value);
  } catch (error) {
    alertFailure(error, 'META_AGENT.OVERVIEW.ONBOARD.ERROR');
  }
};

const checkEligibility = async () => {
  try {
    await store.dispatch('metaAgent/checkEligibility', inboxId.value);
  } catch (error) {
    alertFailure(error, 'META_AGENT.OVERVIEW.ONBOARD.ERROR');
  }
};

const attestTerms = async () => {
  isAttesting.value = true;
  try {
    await store.dispatch('metaAgent/attestTerms', inboxId.value);
  } catch (error) {
    alertFailure(error, 'META_AGENT.OVERVIEW.ONBOARD.ERROR');
  } finally {
    isAttesting.value = false;
  }
};

const onboard = async () => {
  try {
    await store.dispatch('metaAgent/onboard', inboxId.value);
    useAlert(t('META_AGENT.OVERVIEW.ONBOARD.SUCCESS'));
  } catch (error) {
    alertFailure(error, 'META_AGENT.OVERVIEW.ONBOARD.ERROR');
  }
};

const copyAgentId = async () => {
  if (!agent.value.agent_id) return;
  await copyTextToClipboard(agent.value.agent_id);
  useAlert(t('META_AGENT.OVERVIEW.AGENT_ID_COPIED'));
};

const removeAgent = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('META_AGENT.OVERVIEW.DELETE.CONFIRM'))) return;
  try {
    await store.dispatch('metaAgent/deleteAgent', inboxId.value);
    useAlert(t('META_AGENT.OVERVIEW.DELETE.SUCCESS'));
  } catch (error) {
    alertFailure(error, 'META_AGENT.OVERVIEW.DELETE.ERROR');
  }
};

onMounted(load);
watch(inboxId, load);
</script>

<template>
  <PageLayout
    :header-title="t('META_AGENT.OVERVIEW.HEADER')"
    :is-fetching="uiFlags.fetchingAgent"
  >
    <div class="flex flex-col gap-4">
      <StatusCard
        v-if="captainAttending"
        variant="warning"
        :title="t('META_AGENT.OVERVIEW.CAPTAIN_CONFLICT.TITLE')"
        :description="t('META_AGENT.OVERVIEW.CAPTAIN_CONFLICT.DESCRIPTION')"
      />

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatusCard
          :title="t('META_AGENT.OVERVIEW.STATUS')"
          :description="lifecycleLabel"
        />
        <StatusCard
          :title="t('META_AGENT.OVERVIEW.PHONE_NUMBER')"
          :description="inbox.phone_number || '—'"
        />
      </div>

      <StatusCard
        variant="warning"
        :title="t('META_AGENT.OVERVIEW.BILLING_WARNING.TITLE')"
        :description="t('META_AGENT.OVERVIEW.BILLING_WARNING.DESCRIPTION')"
      />

      <StatusCard
        :title="t('META_AGENT.OVERVIEW.ELIGIBILITY.TITLE')"
        :description="
          isEligible === undefined
            ? t('META_AGENT.OVERVIEW.ELIGIBILITY.UNKNOWN')
            : isEligible
              ? t('META_AGENT.OVERVIEW.ELIGIBILITY.ELIGIBLE')
              : t('META_AGENT.OVERVIEW.ELIGIBILITY.NOT_ELIGIBLE')
        "
      >
        <Button
          :label="t('META_AGENT.OVERVIEW.ELIGIBILITY.CHECK')"
          size="sm"
          variant="faded"
          color="slate"
          :is-loading="uiFlags.checkingEligibility"
          @click="checkEligibility"
        />
      </StatusCard>

      <StatusCard
        :title="t('META_AGENT.OVERVIEW.TERMS.TITLE')"
        :description="termsDescription"
      >
        <div class="flex items-center gap-2">
          <a
            :href="WHATSAPP_MANAGER_URL"
            target="_blank"
            rel="noopener noreferrer"
            class="text-sm font-medium whitespace-nowrap text-n-blue-text hover:underline"
          >
            {{ t('META_AGENT.OVERVIEW.TERMS.OPEN_MANAGER') }}
          </a>
          <Button
            v-if="!agent.terms_attested_at"
            :label="t('META_AGENT.OVERVIEW.TERMS.ATTEST')"
            size="sm"
            variant="faded"
            color="slate"
            :is-loading="isAttesting"
            :disabled="captainAttending"
            @click="attestTerms"
          />
        </div>
      </StatusCard>

      <StatusCard
        v-if="!isOnboarded"
        :title="t('META_AGENT.OVERVIEW.ONBOARD.TITLE')"
        :description="t('META_AGENT.OVERVIEW.ONBOARD.DESCRIPTION')"
      >
        <Button
          :label="t('META_AGENT.OVERVIEW.ONBOARD.ACTION')"
          size="sm"
          :is-loading="uiFlags.onboarding"
          :disabled="!agent.terms_attested_at || captainAttending"
          @click="onboard"
        />
      </StatusCard>

      <template v-else>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <StatusCard :title="t('META_AGENT.OVERVIEW.AGENT_ID')">
            <!-- An opaque 100-character id: nobody reads it, they copy it for
                 Meta support. Truncated with the full value on hover. -->
            <template #description>
              <button
                v-tooltip="agent.agent_id"
                type="button"
                class="font-mono text-sm text-left truncate cursor-pointer text-n-slate-11 hover:text-n-slate-12"
                @click="copyAgentId"
              >
                {{ agent.agent_id || '—' }}
              </button>
            </template>
          </StatusCard>
          <StatusCard
            :title="t('META_AGENT.OVERVIEW.LAST_SYNCED')"
            :description="
              agent.last_synced_at
                ? messageStamp(agent.last_synced_at, 'LLL d, yyyy hh:mm a')
                : t('META_AGENT.OVERVIEW.NEVER_SYNCED')
            "
          />
        </div>
        <StatusCard
          variant="danger"
          :title="t('META_AGENT.OVERVIEW.DELETE.TITLE')"
          :description="t('META_AGENT.OVERVIEW.DELETE.DESCRIPTION')"
        >
          <Button
            :label="t('META_AGENT.OVERVIEW.DELETE.ACTION')"
            size="sm"
            color="ruby"
            variant="faded"
            @click="removeAgent"
          />
        </StatusCard>
      </template>
    </div>
  </PageLayout>
</template>
